import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Button, Card,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, IconButton, Tooltip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Stack, Collapse, Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { adminClient } from './data/adminClient';
import { resolveTimelineInfo } from '../shared/utils/timelineText';
import { useEventReadOnly } from './adminAccess';

// Must match ACCOMMODATIONS_REQUEST_TASK_TITLE in backend/index.js exactly —
// that's the row whose columns D/E (accommodationInfoDocUrl/accommodationMealFormUrl)
// this form exposes, and it's also matched by exact string elsewhere (teacher
// portal Accommodations page, TASK_STATUS lookup).
const ACCOMMODATIONS_REQUEST_TASK_TITLE = 'Accommodations & Meals Request';

const EMPTY_FORM = { task: '', information: '', date: '', accommodationInfoDocUrl: '', accommodationMealFormUrl: '' };

// Converts whatever's already in the sheet's Date column (most rows already
// follow "Month D, YYYY", but older/malformed free-typed entries may not)
// into the yyyy-mm-dd shape <TextField type="date"> needs to show a
// pre-filled value. Unparseable text just leaves the picker blank rather
// than erroring — re-picking a real date there is exactly the fix.
function toDateInputValue(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Converts the date picker's yyyy-mm-dd value back into the human-readable
// "Month D, YYYY" text every date-parsing function elsewhere in the app
// already expects (the teacher Dashboard's next-deadline logic, the
// backend's due-date lock checks, etc.) — the picker just guarantees a real,
// unambiguous date is always behind that text now, instead of trusting
// free-typed input to happen to match the expected format.
function formatDateForSheet(isoDate) {
  if (!isoDate) return '';
  // Appending a local (no "Z") time-of-day avoids new Date('2027-01-31')'s
  // UTC-midnight parsing shifting the displayed day back by one depending on
  // the admin's own timezone offset from UTC.
  const d = new Date(`${isoDate}T00:00:00`);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function EventTimelineSection({ event }) {
  const readOnly = useEventReadOnly(); // Event Viewer: no add/edit/delete/reorder
  const [rows, setRows] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [timeline, priceRows] = await Promise.all([
        adminClient.getTimeline(),
        adminClient.getPrices(),
      ]);
      setRows(timeline);
      // Same flatten as SchoolLookupSection.js/SchoolRegistrationsSection.js:
      // adminClient.getPrices() returns the Prices-tab row list, reduced to
      // the { [item]: price } map resolveTimelineInfo expects.
      setPrices(Object.fromEntries(priceRows.map(p => [p.item, p.price])));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetches whenever the parent's `event` object changes — that only
  // happens on mount and right after Event Information is saved (see
  // fetchEvents() in AdminDashboard.js's EventInformation), which is exactly
  // when the sheet's "Event" row date (synced server-side from the saved
  // start/end dates) actually needs picking back up. Without this, editing
  // the event's dates in the popup updated the Event Information card
  // (fed from the same refetch) but left this already-mounted section
  // showing whatever it fetched on its own initial mount.
  useEffect(() => { fetchRows(); }, [fetchRows, event]);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setFormError(null);
    setEditDialog({ mode: 'add' });
  };

  const openEdit = (row) => {
    // The "Event" row holds a date *range* (e.g. "May 25 – May 28, 2027"),
    // not a single date — forcing that through toDateInputValue would just
    // fail to parse and blank the picker, so it keeps its raw text instead
    // (the field is disabled for this row regardless — see isEventRow below).
    const isEvent = row.task.trim() === 'Event';
    setFormData({
      task: row.task,
      information: row.information,
      date: isEvent ? row.date : toDateInputValue(row.date),
      accommodationInfoDocUrl: row.accommodationInfoDocUrl || '',
      accommodationMealFormUrl: row.accommodationMealFormUrl || '',
    });
    setFormError(null);
    setEditDialog({ mode: 'edit', row });
  };

  const isAccommodationsRequest = formData.task.trim() === ACCOMMODATIONS_REQUEST_TASK_TITLE;
  // The "Event" row's Date is auto-generated from Event Information's start/
  // end dates (see syncEventDatesToTimeline in backend/index.js) — editable
  // here would just get silently overwritten the next time Event Information
  // is saved, so the field is disabled rather than letting that surprise
  // someone.
  const isEventRow = formData.task.trim() === 'Event';

  const toggleLinks = (rowIndex) => setExpandedRow(prev => (prev === rowIndex ? null : rowIndex));

  const handleSave = async () => {
    if (!formData.task.trim()) {
      setFormError('Task is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const isEdit = editDialog.mode === 'edit';
      const payload = {
        task: formData.task,
        information: formData.information,
        date: isEventRow ? formData.date : formatDateForSheet(formData.date),
      };
      // Only sent (and thus only ever written) when editing this exact row —
      // every other timeline row leaves columns D/E untouched.
      if (isAccommodationsRequest) {
        payload.accommodationInfoDocUrl = formData.accommodationInfoDocUrl || '';
        payload.accommodationMealFormUrl = formData.accommodationMealFormUrl || '';
      }
      if (isEdit) {
        await adminClient.updateTimelineItem(editDialog.row.rowIndex, payload);
      } else {
        await adminClient.createTimelineItem(payload);
      }
      setEditDialog(null);
      await fetchRows();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await adminClient.deleteTimelineItem(deleteConfirm.rowIndex);
      setDeleteConfirm(null);
      await fetchRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Collapse any open "extra info" panel before dragging starts, since its
  // row doesn't share the dragged row's transform and would visually lag.
  const handleDragStart = () => setExpandedRow(null);

  const handleDragEnd = async (dndEvent) => {
    const { active, over } = dndEvent;
    if (reordering || !over || active.id === over.id) return;
    const oldIndex = rows.findIndex(r => r.rowIndex === active.id);
    const newIndex = rows.findIndex(r => r.rowIndex === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const previousRows = rows;
    const reorderedRows = arrayMove(rows, oldIndex, newIndex);
    setRows(reorderedRows); // optimistic
    setReordering(true);
    try {
      await adminClient.reorderTimeline(reorderedRows.map(r => r.rowIndex));
      await fetchRows(); // pick up canonical rowIndexes for the new order
    } catch (err) {
      setRows(previousRows);
      setError(`Failed to save new order: ${err.message}`);
    } finally {
      setReordering(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Tasks and due dates shown to schools on their teacher-portal timeline.{readOnly ? '' : ' Drag the handle to reorder.'}
        </Typography>
        {!readOnly && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            Add Item
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography fontWeight={500} gutterBottom>No timeline items yet</Typography>
          <Typography variant="body2">Add the first task to build out the event timeline.</Typography>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ width: 32 }} />
                <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Information</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <SortableContext items={rows.map(r => r.rowIndex)} strategy={verticalListSortingStrategy}>
                <TableBody>
                  {rows.map(row => (
                    <TimelineRow
                      key={row.rowIndex}
                      row={row}
                      prices={prices}
                      expandedRow={expandedRow}
                      toggleLinks={toggleLinks}
                      openEdit={openEdit}
                      onDeleteRequest={setDeleteConfirm}
                      readOnly={readOnly}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </DndContext>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={!!editDialog} onClose={() => !saving && setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editDialog?.mode === 'add' ? 'Add Timeline Item' : 'Edit Timeline Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Task *" size="small" fullWidth
              value={formData.task}
              onChange={e => setFormData(prev => ({ ...prev, task: e.target.value }))}
            />
            <TextField
              label="Information" size="small" fullWidth multiline minRows={2}
              placeholder="e.g. A {{Participation Fee}} fee is due per dancer by this date."
              helperText={
                Object.keys(prices).length
                  ? `Write {{Item Name}} to pull a live price instead of typing a dollar amount — it'll always match the Prices tab. Available: ${Object.keys(prices).map(name => `{{${name}}}`).join(', ')}`
                  : 'Write {{Item Name}} (matching a Prices tab item) to pull a live price instead of typing a dollar amount.'
              }
              value={formData.information}
              onChange={e => setFormData(prev => ({ ...prev, information: e.target.value }))}
            />
            {formData.information && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
                Preview: {resolveTimelineInfo(formData.information, prices)}
              </Typography>
            )}
            {isEventRow && (
              <Alert severity="info" sx={{ fontSize: 13 }}>
                This date is generated automatically from Event Information's start and end dates — edit it there instead.
              </Alert>
            )}
            <TextField
              label="Date" size="small" fullWidth
              type={isEventRow ? 'text' : 'date'}
              InputLabelProps={isEventRow ? undefined : { shrink: true }}
              helperText={isEventRow ? 'Auto-generated — see Event Information.' : undefined}
              value={formData.date}
              disabled={isEventRow}
              onChange={e => setFormData(prev => ({ ...prev, date: e.target.value }))}
            />
            {isAccommodationsRequest && (
              <>
                <Alert severity="info" sx={{ fontSize: 13 }}>
                  This task title is matched exactly by the teacher portal's Accommodations page — renaming it will break that link.
                </Alert>
                <TextField
                  label="Accommodations & Meals Information Document URL" size="small" fullWidth
                  value={formData.accommodationInfoDocUrl}
                  onChange={e => setFormData(prev => ({ ...prev, accommodationInfoDocUrl: e.target.value }))}
                />
                <TextField
                  label="Accommodations & Meals Request Form URL" size="small" fullWidth
                  value={formData.accommodationMealFormUrl}
                  onChange={e => setFormData(prev => ({ ...prev, accommodationMealFormUrl: e.target.value }))}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : editDialog?.mode === 'add' ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => !deleting && setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Timeline Item</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deleteConfirm?.task}</strong> from the timeline? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Split out so useSortable's hook count stays fixed per component instance
// (one row = one component instance) rather than varying with rows.length
// inside a single .map() call in the parent.
function TimelineRow({ row, prices, expandedRow, toggleLinks, openEdit, onDeleteRequest, readOnly }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.rowIndex, disabled: readOnly });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.5 } : {}),
  };
  const hasLinks = !!(row.accommodationInfoDocUrl || row.accommodationMealFormUrl);
  const linksOpen = expandedRow === row.rowIndex;

  return (
    <>
      <TableRow ref={setNodeRef} style={style} hover>
        <TableCell sx={{ px: 0.5 }}>
          {!readOnly && (
            <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab', touchAction: 'none' }}>
              <DragIndicatorIcon fontSize="small" color="action" />
            </IconButton>
          )}
        </TableCell>
        <TableCell><Typography variant="body2" fontWeight={500}>{row.task}</Typography></TableCell>
        <TableCell><Typography variant="body2" color="text.secondary">{resolveTimelineInfo(row.information, prices)}</Typography></TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            <Box sx={{ width: 32, display: 'flex', justifyContent: 'center' }}>
              {hasLinks && (
                <Tooltip title={linksOpen ? 'Hide extra info' : 'Has extra info'}>
                  <IconButton size="small" color={linksOpen ? 'primary' : 'default'} onClick={() => toggleLinks(row.rowIndex)}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {!readOnly && (
              <>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEdit(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => onDeleteRequest(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>
      {hasLinks && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0, borderBottom: linksOpen ? undefined : 'none' }}>
            <Collapse in={linksOpen}>
              <Stack spacing={1} sx={{ pl: 4, pr: 2, py: 2 }}>
                {row.accommodationInfoDocUrl && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>Information document:</Typography>
                    <Link href={row.accommodationInfoDocUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {row.accommodationInfoDocUrl}
                    </Link>
                  </Box>
                )}
                {row.accommodationMealFormUrl && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>Request form:</Typography>
                    <Link href={row.accommodationMealFormUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {row.accommodationMealFormUrl}
                    </Link>
                  </Box>
                )}
              </Stack>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
